/** @format */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { FudisSelectOption } from '@funidata/ngx-fudis';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslocoService } from '@ngneat/transloco';
import { ValidatablePlan } from 'common-typescript';
import { AssessmentItem, CompletionMethod, CourseUnit, GradeRaiseAttempt, LocalId, OtmId, SisValidatorFn } from 'common-typescript/types';
import * as _ from 'lodash-es';
import { forkJoin, mergeMap, Observable, pairwise, startWith, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { LocalizedStringPipe, ModalService } from 'sis-common';
import {
    AlertsService,
    AlertType,
    AppErrorHandler,
    AssessmentItemEntityService,
    CourseUnitDisplayNamesById,
    CourseUnitEntityService,
    CourseUnitInfoService,
    CourseUnitInfoVersion,
    CreditRangePipe,
    IntRangePipe,
    PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
    PlanActionsService,
    PlanManager,
    required,
    SisFormBuilder,
    sisuChain,
} from 'sis-components';
import { StudentPlanActionsService } from '../../common/service/student-plan-actions.service';

export interface CompletionMethodSelections {
    selectedCompletionMethod: CompletionMethod;
    selectedAssessmentItemIds: OtmId[];
    gradeRaiseAttempt: GradeRaiseAttempt;
    cancelGradeRaiseAttempt: boolean;
    validatablePlan: ValidatablePlan;
}

export interface SelectCompletionMethodDialogValues {
    completionMethodId: LocalId;
    assessmentItemSelections: OtmId[];
    courseUnit: CourseUnit;
    validatablePlan: ValidatablePlan;
    isGradeRaiseAttempt?: boolean;
    plannedCourseUnitId?: OtmId;
    currentAttainmentId: OtmId;
    gradeRaiseAttempt: GradeRaiseAttempt;
}

@Component({
    selector: 'app-select-completion-method',
    templateUrl: './select-completion-method.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: PLAN_ACTIONS_SERVICE_INJECTION_TOKEN,
            useClass: StudentPlanActionsService,
        },
        PlanManager,
    ],
    standalone: false,
})
export class SelectCompletionMethodComponent implements OnInit {
    _values: SelectCompletionMethodDialogValues;
    form: FormGroup;
    assessmentItems$: Observable<AssessmentItem[][]>;
    completionMethodAssessmentItemsRequireRange: Map<
        LocalId,
        { requireMin: number; requireMax: number; minCredits: number; maxCredits: number }
    > = new Map();
    options$: Observable<FudisSelectOption[]>;
    versionControl = new FormControl<FudisSelectOption | null>(null);
    popoverShown = false;

    readonly gradeRaiseAttemptControlValue = 'cancel_grade_raise_attempt';

    private versionSelectOptions: FudisSelectOption[] = [];

    constructor(
        @Inject(ModalService.injectionToken) private values: SelectCompletionMethodDialogValues,
        public activeModal: NgbActiveModal,
        private fb: SisFormBuilder,
        private translocoService: TranslocoService,
        private creditRangePipe: CreditRangePipe,
        private localizedString: LocalizedStringPipe,
        private assessmentItemEntityService: AssessmentItemEntityService,
        private alertsService: AlertsService,
        private intRangePipe: IntRangePipe,
        private courseUnitInfoService: CourseUnitInfoService,
        private courseUnitEntityService: CourseUnitEntityService,
        private appErrorHandler: AppErrorHandler,
        private readonly planManager: PlanManager,
        @Inject(PLAN_ACTIONS_SERVICE_INJECTION_TOKEN) private readonly planActionsService: PlanActionsService,
        private cdr: ChangeDetectorRef,
        private readonly destroyRef: DestroyRef,
    ) {
        this._values = values;
    }

    ngOnInit(): void {
        this.planManager.setValidatablePlan(_.cloneDeep(this._values.validatablePlan));
        this.createOperationSubscription();
        if (this._values.isGradeRaiseAttempt && !this._values.gradeRaiseAttempt) {
            this.initCourseUnitVersionOptions();
            this.versionControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(option => {
                if (option && option.value !== this._values.courseUnit.id) {
                    this.changeVersion(option.value);
                }
            });
        }

        this.assessmentItems$ = this.initData();
    }

    createOperationSubscription(): Subscription {
        return this.planActionsService.planOperationSubject
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                mergeMap(operation => this.planManager.processPlanOperation(operation)),
            )
            .subscribe();
    }

    initCourseUnitVersionOptions() {
        this.options$ = this.getSelectableVersionsAndNames().pipe(
            this.appErrorHandler.defaultErrorHandler(), // Apply a default error handler
            map(([courseUnitVersions, displayNamesByCourseUnitId]) => {
                const sortedCourseUnitVersions = _.sortBy(courseUnitVersions, 'curriculumPeriods[0].activePeriod.startDate');

                const indexOfAttainedCourseUnit = _.findIndex(
                    sortedCourseUnitVersions,
                    courseUnitVersion => courseUnitVersion.courseUnit.id === this._values.courseUnit.id,
                );

                const filteredCourseUnitVersions = sortedCourseUnitVersions.slice(indexOfAttainedCourseUnit);

                return this.courseUnitInfoService.createSelectOptions(filteredCourseUnitVersions, displayNamesByCourseUnitId);
            }),
            tap(options => {
                this.versionSelectOptions = options;
                this.setVersionControlValue();
            }),
        );
    }

    private setVersionControlValue(): void {
        const selectedOption = this.versionSelectOptions.find(option => option.value === this._values.courseUnit.id);
        this.versionControl.setValue(selectedOption ?? null, { emitEvent: false });
    }

    initData() {
        return forkJoin(
            this._values.courseUnit.completionMethods.map(cm => this.calculateCompletionMethodAssessmentItemCreditsMinMax(cm)),
        ).pipe(
            tap(() => {
                this.form = this.fb.group({
                    selectCompletionMethod: this.fb.sisFormControl(
                        this._values.completionMethodId,
                        required('SELECT_COMPLETION_METHOD_MODAL.INVALID_COMPLETION_METHOD_ERROR'),
                    ),
                    selectedAssessmentItems: this.fb.array(this._values.assessmentItemSelections.map(aii => this.fb.sisFormControl(aii))),
                });

                this.form.controls['selectCompletionMethod'].valueChanges
                    .pipe(startWith(this._values.completionMethodId), pairwise())
                    .subscribe(([prev, next]: [any, any]) => this.completionMethodChanged(prev, next));
            }),
        );
    }

    getAssessmentItemsValidator(completionMethodId: LocalId): SisValidatorFn {
        const requirements = this.completionMethodAssessmentItemsRequireRange.get(completionMethodId);
        if (requirements) return this.createAssessmentItemsValidator(requirements.requireMin, requirements.requireMax);
        return null;
    }

    createAssessmentItemsValidator(min: number, max: number): SisValidatorFn {
        return (control: FormArray) =>
            control.controls.length >= min && control.controls.length <= max
                ? null
                : {
                      requiredValues: {
                          translationKey: 'SELECT_COMPLETION_METHOD_MODAL.INVALID_ASSESSMENT_ITEMS_ERROR',
                          translationParams: {
                              amount: this.intRangePipe.transform({ min, max }),
                          },
                      },
                  };
    }

    completionMethodChanged(prev: any, next: any) {
        if (prev !== next) {
            const selectedAssessmentItems = this.selectedAssessmentItems();
            const completionMethod = this.getCompletionMethods().find(
                cm => cm.localId === next && cm.typeOfRequire === 'ALL_SELECTED_REQUIRED',
            );

            if (prev != null) {
                this.selectedAssessmentItems().clear();
            }

            this.selectedAssessmentItems().clearValidators();
            this.selectedAssessmentItems().markAsUntouched();

            if (!this._values.isGradeRaiseAttempt) this.planActionsService.selectCompletionMethod(this._values.courseUnit, next);
            if (completionMethod) {
                completionMethod.assessmentItemIds.forEach(aii => selectedAssessmentItems.push(this.fb.sisFormControl(aii)));
            }

            if (this.isCancelGradeRaiseAttempt && this._values.plannedCourseUnitId)
                this.planActionsService.removeGradeRaiseAttempt(this._values.plannedCourseUnitId);
        }
    }

    private readonly isCancelGradeRaiseAttempt = () =>
        !!(this._values.isGradeRaiseAttempt && this.selectCompletionMethod().value === this.gradeRaiseAttemptControlValue);

    isAssessmentItemSelectedInCompletionMethod(assessmentItemId: OtmId, completionMethod: CompletionMethod) {
        if (this.selectCompletionMethod().value !== completionMethod.localId) return false;

        const assessmentItemSelection = _.find(this.selectedAssessmentItems().value, aii => aii === assessmentItemId);
        return !!assessmentItemSelection;
    }

    /**
     * Returns pre-translated completion method label.
     */
    getCompletionMethodLabel(completionMethod: CompletionMethod, index: number): string {
        const translatedLabel = this.translocoService.translate('COMPLETION_METHOD');
        const completionMethodRequirements = this.completionMethodAssessmentItemsRequireRange.get(completionMethod.localId);
        return `${translatedLabel} ${index + 1} (${this.creditRangePipe.transform({ min: completionMethodRequirements.minCredits, max: completionMethodRequirements.maxCredits })})`;
    }

    getAssessmentItemLabel(assessmentItem: AssessmentItem): string {
        return `${this.localizedString.transform(assessmentItem.name)} (${this.creditRangePipe.transform(assessmentItem.credits)})`;
    }

    assessmentItemCheckboxClicked(event: boolean, completionMethod: CompletionMethod, assessmentItemId: OtmId) {
        if (this.selectCompletionMethod().value !== completionMethod.localId) {
            this.updateCompletionMethodSelection(completionMethod);
        }
        const selectedAssessmentItems = this.selectedAssessmentItems();

        if (event) {
            if (!this._values.isGradeRaiseAttempt)
                this.planActionsService.addAssessmentItemSelection(this._values.courseUnit, assessmentItemId);
            selectedAssessmentItems.push(this.fb.control(assessmentItemId));
        } else {
            if (!this._values.isGradeRaiseAttempt)
                this.planActionsService.removeAssessmentItemSelection(this._values.courseUnit, assessmentItemId);
            selectedAssessmentItems.removeAt(selectedAssessmentItems.value.findIndex((aii: any) => aii === assessmentItemId));
        }

        selectedAssessmentItems.markAsTouched();
        selectedAssessmentItems.updateValueAndValidity();
    }

    updateCompletionMethodSelection(completionMethod: CompletionMethod) {
        this.selectCompletionMethod().setValue(completionMethod.localId);
    }

    getCompletionMethods() {
        return this._values.courseUnit?.completionMethods.filter((cm: CompletionMethod) => cm.studyType !== 'OPEN_UNIVERSITY_STUDIES');
    }

    selectCompletionMethod(): FormControl {
        return this.form.get('selectCompletionMethod') as FormControl;
    }

    getAriaDescribedByIndex(index: number): string {
        return `range-help-${index} description-help-${index}`;
    }

    selectedAssessmentItems(): FormArray {
        return this.form.get('selectedAssessmentItems') as FormArray;
    }

    getSelectableVersionsAndNames(): Observable<[CourseUnitInfoVersion[], CourseUnitDisplayNamesById]> {
        return this.courseUnitInfoService.getSelectableVersionsAndNamesByCu(this._values.courseUnit);
    }

    changeVersion(courseUnitId: OtmId): void {
        this.getCourseUnit(courseUnitId).subscribe((courseUnit: CourseUnit) => {
            this._values.courseUnit = courseUnit;
            this.setVersionControlValue();
            this.initData().subscribe(() => {
                this.cdr.markForCheck();
            });
        });
    }

    getCourseUnit(courseUnitId: OtmId) {
        return this.courseUnitEntityService.getById(courseUnitId).pipe(take(1), this.appErrorHandler.defaultErrorHandler());
    }

    submit() {
        this.validateForm();

        if (!this.form.valid) {
            this.handleFormErrors();
            return;
        }

        const cancelGradeRaiseAttempt = this.isCancelGradeRaiseAttempt();
        const result = this.createSubmissionResult(cancelGradeRaiseAttempt);

        this.closeModalWithResult(result, cancelGradeRaiseAttempt);
    }

    private validateForm(): void {
        this.selectCompletionMethod().updateValueAndValidity();

        const selectedCompletionMethod = this.getSelectedCompletionMethod();
        if (selectedCompletionMethod) {
            const validator = this.getAssessmentItemsValidator(selectedCompletionMethod.localId);
            this.selectedAssessmentItems().setValidators(validator);
        }

        this.form.markAllAsTouched();
        this.selectedAssessmentItems().updateValueAndValidity();
    }

    private getSelectedCompletionMethod(): CompletionMethod | undefined {
        return this.getCompletionMethods().find(cm => cm.localId === this.selectCompletionMethod().value);
    }

    private createSubmissionResult(cancelGradeRaiseAttempt: boolean): CompletionMethodSelections {
        if (cancelGradeRaiseAttempt) {
            return this.createCancelResult();
        }

        const selectedCompletionMethod = this.getSelectedCompletionMethod();
        const gradeRaiseAttempt = this.createGradeRaiseAttempt(selectedCompletionMethod);

        if (gradeRaiseAttempt && this._values.plannedCourseUnitId) {
            this.planActionsService.addGradeRaiseAttempt(this._values.plannedCourseUnitId, gradeRaiseAttempt);
        }

        return {
            selectedCompletionMethod,
            selectedAssessmentItemIds: this.selectedAssessmentItems().value,
            gradeRaiseAttempt,
            cancelGradeRaiseAttempt: false,
            validatablePlan: this.planManager.validatablePlan,
        };
    }

    private createCancelResult(): CompletionMethodSelections {
        return {
            selectedCompletionMethod: null,
            selectedAssessmentItemIds: [],
            gradeRaiseAttempt: null,
            cancelGradeRaiseAttempt: true,
            validatablePlan: this.planManager.validatablePlan,
        };
    }

    private createGradeRaiseAttempt(selectedCompletionMethod: CompletionMethod): GradeRaiseAttempt | null {
        if (!this._values.isGradeRaiseAttempt) {
            return null;
        }

        return {
            completionMethodId: selectedCompletionMethod.localId,
            originalAttainmentId: this._values.currentAttainmentId,
            courseUnitId: this._values.courseUnit.id,
            selectedAssessmentItemIds: this.selectedAssessmentItems().value,
        };
    }

    private closeModalWithResult(result: CompletionMethodSelections, cancelGradeRaiseAttempt: boolean): void {
        this.activeModal.close(result);
        this.showSuccessAlert(cancelGradeRaiseAttempt);
    }

    private showSuccessAlert(cancelGradeRaiseAttempt: boolean): void {
        const alertTranslationKey = cancelGradeRaiseAttempt
            ? 'SELECT_COMPLETION_METHOD_MODAL.GRADE_RAISE_CANCEL_SUCCESS_ALERT'
            : 'SELECT_COMPLETION_METHOD_MODAL.SUCCESS_ALERT';
        const alertType = cancelGradeRaiseAttempt ? AlertType.SUCCESS : AlertType.INFO;

        this.alertsService.addTemporaryAlert({
            message: this.translocoService.translate(alertTranslationKey),
            type: alertType,
        });
    }

    private handleFormErrors(): void {
        const selectedCompletionMethod = this.getSelectedCompletionMethod();

        if (this.selectCompletionMethod().invalid) {
            document.getElementById('select-completion-method')?.focus();
        } else if (this.selectedAssessmentItems().invalid && selectedCompletionMethod) {
            document.getElementById(selectedCompletionMethod.localId)?.focus();
        }
    }
    calculateCompletionMethodAssessmentItemCreditsMinMax(completionMethod: CompletionMethod): Observable<AssessmentItem[]> {
        return this.assessmentItemEntityService.getByIds(completionMethod.assessmentItemIds).pipe(
            take(1),
            tap(assessmentItems => {
                let requireMin;
                let requireMax;

                if (completionMethod.typeOfRequire === 'OPTIONAL_WITH_REQUIRE_RANGE') {
                    requireMin = _.get(completionMethod, 'require.min');
                    requireMax = _.get(completionMethod, 'require.max');
                } else if (completionMethod.typeOfRequire === 'OPTIONAL_WITH_DESCRIPTION') {
                    requireMin = 1;
                    requireMax = assessmentItems.length;
                } else {
                    requireMin = assessmentItems.length;
                    requireMax = assessmentItems.length;
                }

                const sortedMinCredits = sisuChain(assessmentItems)
                    .map((item: any) => _.get(item, 'credits.min'))
                    .compact()
                    .sortBy()
                    .value();

                const minCredits = (_.take(sortedMinCredits, requireMin) as number[]).reduce((acc, val) => acc + val, 0) as number;

                const sortedMaxCredits = sisuChain(assessmentItems)
                    .map((item: any) => _.get(item, 'credits.max'))
                    .compact()
                    .sortBy()
                    .reverse()
                    .value();

                const maxCredits = (_.take(sortedMaxCredits, requireMax) as number[]).reduce((acc, val) => acc + val, 0) as number;

                this.completionMethodAssessmentItemsRequireRange.set(completionMethod.localId, {
                    minCredits,
                    maxCredits,
                    requireMin,
                    requireMax,
                });
            }),
        );
    }

    cancel() {
        this.activeModal.dismiss();
    }

    isOpen() {
        this.popoverShown = true;
    }

    isHidden() {
        this.popoverShown = false;
    }
}
